<div class="invoice-history-container">
  <!-- Toolbar: type toggles + hide completed + test add -->
  <div class="invoice-toolbar">
    <mat-chip-set class="type-filter" aria-label="Filter by document type">
      <mat-chip
        class="type-chip"
        [class.chip-selected]="isAllTypes"
        (click)="selectAllTypes()"
      >All</mat-chip>
      <mat-chip
        *ngFor="let chip of visibleTypeChips"
        class="type-chip"
        [class.chip-selected]="isTypeSelected(chip.key)"
        (click)="toggleType(chip.key)"
      >{{ chip.label }}</mat-chip>
    </mat-chip-set>

    <div class="toolbar-right">
      <div class="invoice-search">
        <mat-icon class="search-icon">search</mat-icon>
        <input
          type="text"
          placeholder="Search invoice #"
          [(ngModel)]="searchTerm"
          (ngModelChange)="onSearchChange()"
          aria-label="Search invoices by number"
        />
        <button
          *ngIf="searchTerm"
          class="search-clear"
          type="button"
          (click)="searchTerm = ''; onSearchChange()"
          aria-label="Clear search"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-slide-toggle
        [(ngModel)]="hideCompleted"
        (change)="onHideCompletedChange()"
        matTooltip="Hide fully-paid (zero-balance) documents"
      >
        Hide Completed
      </mat-slide-toggle>

      <button
        *ngIf="isTestInvoicesEnabled()"
        mat-raised-button
        color="accent"
        (click)="openTestAddInvoicesModal()"
      >
        <mat-icon>science</mat-icon>
        Test Add
      </button>
    </div>
  </div>

  <!-- Loading State — only when there is nothing on screen yet. A reload of an
       existing list (sort, page, filter) keeps the table and shows a bar above
       it instead: removing the table would also reset its sort header. -->
  <div class="loading-state" *ngIf="isLoading && invoices.length === 0">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading invoices...</p>
  </div>

  <!-- Invoice Table -->
  <div class="table-container" *ngIf="invoices.length > 0">
    <mat-progress-bar *ngIf="isLoading" mode="indeterminate"></mat-progress-bar>
    <!-- matSortDisableClear: a column alternates ascending/descending and never
         drops back to "unsorted" on a third click. -->
    <table
      mat-table
      [dataSource]="invoices"
      matSort
      [matSortActive]="sortActive"
      [matSortDirection]="sortDirection"
      matSortDisableClear
      (matSortChange)="onSortChange($event)"
      class="invoice-history-table"
      multiTemplateDataRows
    >
      <!-- Expand Column -->
      <ng-container matColumnDef="expand">
        <th mat-header-cell *matHeaderCellDef class="expand-column"></th>
        <td mat-cell *matCellDef="let item" class="expand-column">
          <button
            mat-icon-button
            (click)="toggleExpand(item)"
            matTooltip="Click to view invoice details &amp; history"
          >
            <mat-icon>
              {{ isExpanded(item.invoiceId) ? 'expand_less' : 'expand_more' }}
            </mat-icon>
          </button>
        </td>
      </ng-container>

      <!-- Invoice Number Column -->
      <ng-container matColumnDef="invoiceNumber">
        <th mat-header-cell *matHeaderCellDef>Invoice #</th>
        <td mat-cell *matCellDef="let item">
          <div class="invoice-number">{{ item.invoiceNumber }}</div>
          <div class="invoice-description" *ngIf="item.description">{{ item.description }}</div>
        </td>
      </ng-container>

      <!-- Invoice Type Column -->
      <ng-container matColumnDef="invoiceType">
        <th mat-header-cell *matHeaderCellDef>Type</th>
        <td mat-cell *matCellDef="let item">
          <span *ngIf="item.invoiceType" class="invoice-type-badge">{{ invoiceTypeLabel(item.invoiceType) }}</span>
          <span *ngIf="!item.invoiceType">—</span>
        </td>
      </ng-container>

      <!-- Date Column -->
      <ng-container matColumnDef="date">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Invoice Date</th>
        <td mat-cell *matCellDef="let item">
          <div class="date-info">
            <div>{{ item.invoiceDate | date : 'MMM dd, yyyy' }}</div>
            <div class="due-date">Due: {{ item.dueDate | date : 'MMM dd, yyyy' }}</div>
          </div>
        </td>
      </ng-container>

      <!-- Total Amount Column -->
      <ng-container matColumnDef="amount">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="text-right">Total Amount</th>
        <td mat-cell *matCellDef="let item" class="text-right amount-column">
          {{ item.amount | currency }}
        </td>
      </ng-container>

      <!-- Total Paid Column -->
      <ng-container matColumnDef="totalPaid">
        <th mat-header-cell *matHeaderCellDef class="text-right">Total Paid / Applied</th>
        <td mat-cell *matCellDef="let item" class="text-right paid-column">
          {{ paidOrApplied(item) | currency }}
        </td>
      </ng-container>

      <!-- Balance Column -->
      <ng-container matColumnDef="balance">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="text-right">Balance</th>
        <td mat-cell *matCellDef="let item" class="text-right balance-column">
          <span [class.balance-zero]="item.balance === 0">
            {{ item.balance | currency }}
          </span>
        </td>
      </ng-container>

      <!-- Status Column -->
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>Status</th>
        <td mat-cell *matCellDef="let item">
          <mat-chip [class]="'status-chip status-' + item.status">
            {{ getStatusLabel(item.status) }}
          </mat-chip>
          <!-- Why it cannot be used: the ERP dropped it and nothing explains it as settled.
               An invoice reads Not Payable, a credit memo Not Usable — one badge for both. -->
          <span
            *ngIf="isErpUnlisted(item)"
            class="not-payable-chip"
            [matTooltip]="notPayableHint(item)"
          >
            <mat-icon aria-hidden="true">money_off</mat-icon>
            Not in ERP
          </span>
        </td>
      </ng-container>

      <!-- Ledger Column (profiles that keep a customer ledger) -->
      <ng-container matColumnDef="ledger">
        <th
          mat-header-cell
          *matHeaderCellDef
          matTooltip="How the customer ledger compares with the balance the ERP states for this document"
        >
          Ledger
        </th>
        <td mat-cell *matCellDef="let item">
          <span
            *ngIf="item.ledgerStatus; else noLedgerStatus"
            [class]="ledgerStatusClass(item.ledgerStatus)"
            [matTooltip]="ledgerStatusHint(item)"
            >{{ ledgerStatusLabel(item.ledgerStatus) }}</span
          >
          <ng-template #noLedgerStatus><span class="ledger-chip" [matTooltip]="ledgerStatusHint(item)">—</span></ng-template>
          <div class="ledger-variance" *ngIf="item.ledgerVariance !== null && item.ledgerVariance !== undefined && item.ledgerStatus !== 'Balanced' && item.ledgerStatus !== 'NoLedger'">
            {{ item.ledgerVariance | currency }}
          </div>
        </td>
      </ng-container>

      <!-- Actions Column -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef class="text-right">Actions</th>
        <td mat-cell *matCellDef="let item" class="text-right actions-column">
          <button
            mat-button
            color="primary"
            (click)="collectPayment(item.invoiceId)"
            *ngIf="item.balance && item.balance > 0 && !isErpUnlisted(item)"
            [disabled]="!hasPaymentMethods"
            [matTooltip]="!hasPaymentMethods ? 'No payment method on file. Please add a payment method first.' : ''"
            matTooltipPosition="left"
          >
            Collect
          </button>
        </td>
      </ng-container>

      <!-- Header and Row Declarations -->
      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns"
        [class.row-expanded]="isExpanded(row.invoiceId)"
      ></tr>

      <!-- Expanded Content Row: invoice history event timeline -->
      <ng-container matColumnDef="expandedDetail">
        <td mat-cell *matCellDef="let item" [attr.colspan]="displayedColumns.length">
          <div class="expanded-detail" [@detailExpand]="isExpanded(item.invoiceId) ? 'expanded' : 'collapsed'">
            <div class="invoice-detail-block">
              <h4 class="timeline-title">Invoice Details</h4>
              <div class="not-payable-notice" *ngIf="isErpUnlisted(item)" role="note">
                <mat-icon aria-hidden="true">money_off</mat-icon>
                <div>
                  <strong>{{ erpUnlistedTitle(item) }}</strong> {{ notPayableHint(item) }}
                  <div class="not-payable-meta" *ngIf="item.notPayable as marker">
                    Marked {{ marker.markedAt | date : 'MMM dd, yyyy h:mm a' }} · balance when marked
                    {{ marker.outstandingAtMark | currency }}
                  </div>
                </div>
              </div>
              <div class="detail-grid">
                <div class="detail-item"><span class="detail-label">Invoice #</span><span class="detail-value">{{ item.invoiceNumber }}</span></div>
                <div class="detail-item"><span class="detail-label">Type</span><span class="detail-value">{{ invoiceTypeLabel(item.invoiceType) }}</span></div>
                <div class="detail-item"><span class="detail-label">Status</span><span class="detail-value">{{ getStatusLabel(item.status) }}</span></div>
                <div class="detail-item"><span class="detail-label">Invoice Date</span><span class="detail-value">{{ item.invoiceDate | date : 'MMM dd, yyyy' }}</span></div>
                <div class="detail-item"><span class="detail-label">Due Date</span><span class="detail-value">{{ item.dueDate | date : 'MMM dd, yyyy' }}</span></div>
                <div class="detail-item"><span class="detail-label">Account #</span><span class="detail-value">{{ item.customerAccountNumber || '—' }}</span></div>
                <div class="detail-item"><span class="detail-label">Total Amount</span><span class="detail-value">{{ item.amount | currency }}</span></div>
                <div class="detail-item"><span class="detail-label">{{ isCreditDocument(item) ? 'Total Applied' : 'Total Paid' }}</span><span class="detail-value">{{ paidOrApplied(item) | currency }}</span></div>
                <div class="detail-item"><span class="detail-label">Balance</span><span class="detail-value">{{ item.balance | currency }}</span></div>
                <div class="detail-item">
                  <span class="detail-label">{{ isCreditDocument(item) ? 'Accounted For Applications' : 'Accounted For Payments' }}</span>
                  <span
                    class="detail-value"
                    matTooltip="Sum of the payment lines Freely holds against this document (on a credit, the applications of it). Total Paid / Applied above is what the ERP asserts; these two disagreeing is what Accounting Status reports."
                  >{{ accountedForDisplay(item) === undefined ? '—' : (accountedForDisplay(item) | currency) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Accounting Status</span>
                  <span class="detail-value">
                    <span
                      *ngIf="item.accountingStatus; else noAccountingStatus"
                      [class]="accountingStatusClass(item.accountingStatus)"
                      [matTooltip]="item.accountedForUpdatedAt
                        ? 'Last recomputed ' + (item.accountedForUpdatedAt | date : 'MMM dd, yyyy h:mm a')
                        : 'Not yet recomputed for this document'"
                    >{{ item.accountingStatus }}</span>
                    <ng-template #noAccountingStatus>—</ng-template>
                  </span>
                </div>
                <ng-container *ngIf="ledgerEnabled">
                  <div class="detail-item">
                    <span class="detail-label">Ledger Balance</span>
                    <span class="detail-value">{{ item.ledgerBalance === null || item.ledgerBalance === undefined ? '—' : (item.ledgerBalance | currency) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Ledger Status</span>
                    <span class="detail-value">
                      <span
                        *ngIf="item.ledgerStatus; else noLedgerDetail"
                        [class]="ledgerStatusClass(item.ledgerStatus)"
                        [matTooltip]="item.ledgerCheckedAt ? 'Last compared ' + (item.ledgerCheckedAt | date : 'MMM dd, yyyy h:mm a') : ledgerStatusHint(item)"
                        >{{ ledgerStatusLabel(item.ledgerStatus) }}</span
                      >
                      <ng-template #noLedgerDetail>—</ng-template>
                    </span>
                  </div>
                  <div class="detail-item" *ngIf="item.ledgerVariance !== null && item.ledgerVariance !== undefined">
                    <span class="detail-label">Ledger − ERP</span>
                    <span class="detail-value">{{ item.ledgerVariance | currency }}</span>
                  </div>
                </ng-container>
              </div>
              <button
                *ngIf="ledgerEnabled && canViewLedger"
                mat-button
                color="primary"
                type="button"
                class="view-ledger"
                (click)="openLedger(item)"
              >
                <mat-icon>menu_book</mat-icon>
                View in ledger
              </button>
            </div>

            <!-- Raw import metadata — fetched only when this drawer is opened -->
            <div class="metadata-detail">
              <button
                type="button"
                class="metadata-toggle"
                (click)="toggleMetadata(item)"
                [attr.aria-expanded]="isMetadataOpen(item.invoiceId)"
              >
                <mat-icon>{{ isMetadataOpen(item.invoiceId) ? 'expand_less' : 'expand_more' }}</mat-icon>
                <span class="timeline-title">Metadata</span>
                <span class="metadata-hint">raw imported values</span>
              </button>

              <div class="metadata-body" *ngIf="isMetadataOpen(item.invoiceId)">
                <div class="timeline-loading" *ngIf="isMetadataLoading(item.invoiceId)">
                  <mat-spinner diameter="24"></mat-spinner>
                  <span>Loading metadata…</span>
                </div>

                <div class="tab-empty-state" *ngIf="hasMetadataError(item.invoiceId)">
                  <mat-icon>error_outline</mat-icon>
                  <p>Could not load metadata. Close and reopen to retry.</p>
                </div>

                <ng-container
                  *ngIf="!isMetadataLoading(item.invoiceId) && !hasMetadataError(item.invoiceId)"
                >
                  <div class="metadata-grid" *ngIf="metadataFor(item.invoiceId).length > 0; else noMetadata">
                    <div class="detail-item" *ngFor="let entry of metadataFor(item.invoiceId)">
                      <span class="detail-label">{{ entry.path }}</span>
                      <span class="detail-value metadata-value">{{ entry.value }}</span>
                    </div>
                  </div>

                  <ng-template #noMetadata>
                    <div class="tab-empty-state">
                      <mat-icon>data_object</mat-icon>
                      <!-- Covers both "no metadata at all" and "every field is blank",
                           which look the same to a reader and lead to the same place. -->
                      <p>No metadata values stored for this invoice.</p>
                    </div>
                  </ng-template>
                </ng-container>
              </div>
            </div>

            <div class="timeline-detail">
              <h4 class="timeline-title">Invoice History</h4>

              <div class="timeline-loading" *ngIf="isTimelineLoading(item.invoiceId)">
                <mat-spinner diameter="24"></mat-spinner>
                <span>Loading history…</span>
              </div>

              <ng-container *ngIf="!isTimelineLoading(item.invoiceId)">
                <div class="timeline-list" *ngIf="timelineFor(item.invoiceId).length > 0; else noEvents">
                  <div class="timeline-event" *ngFor="let ev of timelineFor(item.invoiceId)">
                    <mat-icon class="timeline-icon">{{ eventIcon(ev.eventType) }}</mat-icon>
                    <div class="timeline-body">
                      <div class="timeline-row">
                        <span class="timeline-type">{{ eventLabel(ev.eventType) }}</span>
                        <span class="timeline-amount" *ngIf="ev.amount">{{ ev.amount | currency }}</span>
                      </div>
                      <div class="timeline-meta">
                        <span class="timeline-date">{{ ev.eventDateTime | date : 'MMM dd, yyyy h:mm a' }}</span>
                        <span class="timeline-desc" *ngIf="ev.description"> · {{ ev.description }}</span>
                      </div>
                      <div class="timeline-txn" *ngIf="ev.transactionId">Txn: {{ ev.transactionId }}</div>
                    </div>
                  </div>
                </div>

                <ng-template #noEvents>
                  <div class="tab-empty-state">
                    <mat-icon>history</mat-icon>
                    <p>No history events for this invoice.</p>
                  </div>
                </ng-template>
              </ng-container>
            </div>
          </div>
        </td>
      </ng-container>

      <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row"></tr>
    </table>

    <!-- Server-side paginator -->
    <mat-paginator
      [length]="total"
      [pageSize]="pageSize"
      [pageIndex]="pageIndex"
      [pageSizeOptions]="pageSizeOptions"
      (page)="onPage($event)"
      showFirstLastButtons
      aria-label="Select page of invoices"
    >
    </mat-paginator>
  </div>

  <!-- Empty State -->
  <div class="empty-state" *ngIf="!isLoading && invoices.length === 0">
    <mat-icon>receipt_long</mat-icon>
    <h3>No Invoices</h3>
    <p>There are no invoices to display for this customer with the current filters.</p>
  </div>
</div>
